import React, {createContext, useContext, ReactNode} from "react";
import {Text, View} from "react-native";
import type {StyleProp, TextStyle, ViewStyle} from "react-native";
import MainStyle from "../../../MainStyle";

const noticeTableColumnWidths = {side: '20%', examples: '60%'} as const;

const styles = {
  text_l: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_24, MainStyle.m_b_30, MainStyle.l_h_36],
  text_s: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_12, MainStyle.m_b_15, MainStyle.l_h_16],

  small_link: [MainStyle.c_lightblue, MainStyle.Barlow700, MainStyle.t_d_l_u],

  secondary_l: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_18, MainStyle.m_b_30, MainStyle.l_h_36],
  secondary_s: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_10, MainStyle.m_b_15, MainStyle.l_h_16],

  title_l: [MainStyle.c_lightblue, MainStyle.Barlow700, MainStyle.f_s_32, MainStyle.m_b_30, MainStyle.t_t_u],
  title_s: [MainStyle.c_lightblue, MainStyle.Barlow700, MainStyle.f_s_16, MainStyle.m_b_15, MainStyle.t_t_u],

  dot_l: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_24, MainStyle.l_h_36, MainStyle.m_x_16],
  dot_s: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_12, MainStyle.l_h_16, MainStyle.m_x_8]
};

type PrivacyPolicyTextContextValue = {
  textStyle: Array<StyleProp<TextStyle>>, secondaryStyle: Array<StyleProp<TextStyle>>,
  titleStyle: Array<StyleProp<TextStyle>>, dotStyle: Array<StyleProp<TextStyle>>,
  sectionBottomMargin: StyleProp<ViewStyle>, openLink: (url: string) => void, navigateToNotifications: () => void
};

const PrivacyPolicyTextContext = createContext<PrivacyPolicyTextContextValue | null>(null);

const usePrivacyPolicyText = () => {
  const value = useContext(PrivacyPolicyTextContext);

  if (!value) {
    throw new Error('PrivacyPolicyText components must be used inside PrivacyPolicyTextProvider');
  }

  return value;
};

const linkPattern = /(www\.evenplay\.com\/notifications|www\.evenplay\.com|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;

const renderLinkedText = (text: string, openLink: (url: string) => void, navigateToNotifications: () => void) => {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  linkPattern.lastIndex = 0;

  while ((match = linkPattern.exec(text)) !== null) {
    const value = match[0];
    const index = match.index;

    if (index > lastIndex) {
      parts.push(text.slice(lastIndex, index));
    }

    if (value === 'www.evenplay.com/notifications') {
      parts.push(
        <Text key={`${value}-${index}`} style={styles.small_link} onPress={navigateToNotifications}
              accessibilityRole="link">
          {value}
        </Text>
      );
    } else {
      const url = value.includes('@') ? `mailto:${value}` : `https://${value}`;

      parts.push(
        <Text key={`${value}-${index}`} style={styles.small_link} onPress={() => openLink(url)}
              accessibilityRole="link">
          {value}
        </Text>
      );
    }

    lastIndex = index + value.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length ? parts : text;
};

const usePolicyTextChildren = (children: ReactNode) => {
  const {openLink, navigateToNotifications} = usePrivacyPolicyText();

  return typeof children === 'string' ? renderLinkedText(children, openLink, navigateToNotifications) : children;
};

type ProviderProps = {
  children: ReactNode, isSm: boolean, openLink: (url: string) => void, navigateToNotifications: () => void
};

export const PrivacyPolicyTextProvider = (props: ProviderProps) => {
  const {children, isSm, openLink, navigateToNotifications} = props;
  const textStyle = isSm ? styles.text_s : styles.text_l;
  const secondaryStyle = isSm ? styles.secondary_s : styles.secondary_l;
  const titleStyle = isSm ? styles.title_s : styles.title_l;
  const dotStyle = isSm ? styles.dot_s : styles.dot_l;
  const sectionBottomMargin = isSm ? MainStyle.m_b_15 : MainStyle.m_b_30;

  return (
    <PrivacyPolicyTextContext.Provider value={{
      textStyle, secondaryStyle, titleStyle, dotStyle, sectionBottomMargin, openLink, navigateToNotifications
    }}>
      {children}
    </PrivacyPolicyTextContext.Provider>
  );
};

export const Paragraph = ({children, noMargin = false}: { children: ReactNode; noMargin?: boolean }) => {
  const {textStyle} = usePrivacyPolicyText();
  const content = usePolicyTextChildren(children);

  return <Text style={noMargin ? [...textStyle, MainStyle.m_b_0] : textStyle}>{content}</Text>;
};

export const SectionHeading = ({children, nativeID}: { children: ReactNode; nativeID?: string }) => {
  const {textStyle} = usePrivacyPolicyText();
  return <Text style={[...textStyle, MainStyle.t_d_l_u, MainStyle.Barlow700]} nativeID={nativeID}>{children}</Text>;
};

export const SubHeading = ({children}: { children: ReactNode }) => {
  const {textStyle} = usePrivacyPolicyText();
  return <Text style={[...textStyle, MainStyle.Barlow700]}>{children}</Text>;
};

export const UnderlinedParagraph = ({children}: { children: ReactNode }) => {
  const {textStyle} = usePrivacyPolicyText();
  return <Text style={[...textStyle, MainStyle.t_d_l_u]}>{children}</Text>;
};

export const PolicyTitle = ({children, noMargin = false}: { children: ReactNode, noMargin?: boolean }) => {
  const {titleStyle} = usePrivacyPolicyText();
  return <Text style={noMargin ? [...titleStyle, MainStyle.m_b_0] : titleStyle}>{children}</Text>;
};

export const ListItem = (props: { children: ReactNode, marker?: string, withBottomMargin?: boolean }) => {
  const {children, marker = '\u2022', withBottomMargin = false} = props;
  const {dotStyle, sectionBottomMargin, textStyle} = usePrivacyPolicyText();
  const textNoMarginStyle = [...textStyle, MainStyle.m_b_0];
  const content = usePolicyTextChildren(children);

  return (
    <View style={withBottomMargin ? [MainStyle.row, sectionBottomMargin] : MainStyle.row}>
      <Text style={dotStyle}>{marker}</Text>
      <Text style={textNoMarginStyle}>{content}</Text>
    </View>
  );
};

export const NoticeTableHeader = () => {
  const {textStyle} = usePrivacyPolicyText();

  return (
    <View style={[MainStyle.row, MainStyle.b_b_w_1, MainStyle.b_c_white]}>
      <Text style={[...textStyle, MainStyle.m_b_0, MainStyle.t_a_c, {width: noticeTableColumnWidths.side}]}>
        Category
      </Text>
      <Text style={[...textStyle, MainStyle.m_b_0, MainStyle.t_a_c, {width: noticeTableColumnWidths.examples}]}>
        Examples
      </Text>
      <Text style={[...textStyle, MainStyle.m_b_0, MainStyle.t_a_c, {width: noticeTableColumnWidths.side}]}>
        Collected
      </Text>
    </View>
  );
};

export const NoticeTableRow = (props: { category: ReactNode, children: ReactNode, withBottomMargin?: boolean }) => {
  const {category, children, withBottomMargin = false} = props;
  const {secondaryStyle, sectionBottomMargin} = usePrivacyPolicyText();
  const secondaryNoMarginStyle = [...secondaryStyle, MainStyle.m_b_0];

  return (
    <View
      style={withBottomMargin ? [MainStyle.row, sectionBottomMargin] : [MainStyle.row, MainStyle.b_b_w_1, MainStyle.b_c_white]}>
      <Text style={[...secondaryNoMarginStyle, {width: noticeTableColumnWidths.side}]}>
        {category}
      </Text>
      <Text style={[...secondaryNoMarginStyle, {width: noticeTableColumnWidths.examples}]}>
        {children}
      </Text>
      <Text style={[...secondaryNoMarginStyle, {width: noticeTableColumnWidths.side}]}/>
    </View>
  );
};
